import {
  afterNextRender,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild
} from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  RouterLink,
  RouterOutlet,
  RouterLinkActive
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { WorkingDateService } from '../services/working-date.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAnglesLeft, faAnglesRight, faAward, faBars, faCartShopping, faCreditCard,
  faEnvelope, faFileContract, faFileInvoice, faGear, faGrid2, faHouse,
  faLifeRing, faListCheck, faScrewdriverWrench, faSplit, faUser,
  faUsers, faXmark
} from '@fortawesome/pro-light-svg-icons';
import { DOCUMENT, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { version } from '../../../package.json';
import { generateClient } from 'aws-amplify/data';
import { Schema } from '../../../../backend/amplify/data/resource';
import {
  ADMIN_HOME,
  CAPABILITY,
  Capability,
  capabilitiesOf,
  getUserGroups,
  HOME
} from '../shared/utils/auth-groups.util';
import { CommandPaletteService } from '../shared/ui/command-palette/command-palette.service';
import { PaletteSwitcherComponent, PaletteTab } from '../shared/ui/command-palette/palette-switcher.component';

interface User {
  firstName: string;
  lastName: string;
  email: string;
}

interface MenuItem {
  label: string;
  route: string;
  /**
   * La capacité qui fait apparaître l'entrée. Absente : visible de quiconque a
   * au moins une capacité — c'est le cas du seul tableau de bord.
   */
  requires?: Capability;
  /**
   * Icône Font Awesome Pro **Light**, en objet et non en chaîne : c'est ce qui
   * la rend arborescente au bundle.
   */
  icon: IconDefinition;
}

/** Une section du menu, sous son titre en capitales. */
interface NavGroup {
  /** Identifiant DOM du titre, pour `aria-labelledby`. */
  id: string;
  label: string;
  items: MenuItem[];
}

/** Retient dans le navigateur que le menu est replié (une commodité par poste). */
const COLLAPSED_KEY = 'lp-gelo.sidebar-collapsed';

/**
 * La coque des écrans connectés : la barre latérale du standard L&P (celle de
 * LP-FIN-APP et de latouretpetit, reprise le 2026-10-13), la barre du haut sous
 * `md` et quand le menu est replié, et ce qui n'existe qu'ici — la date de
 * travail, le lanceur des palettes, la notification d'intégration.
 */
@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.css'],
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    NgTemplateOutlet,
    NgOptimizedImage,
    RouterLinkActive,
    FormsModule,
    FontAwesomeModule,
    PaletteSwitcherComponent
  ]
})
export class LayoutComponent implements OnInit, OnDestroy {
  currentTitle = 'Application';
  version = version;
  showIntegrationNotification = false;
  pendingIntegrationCount = 0;
  /** Les capacités de l'utilisateur, résolues au démarrage. */
  private capabilities: ReadonlySet<Capability> = new Set();
  modalOpen = false;

  private client = generateClient<Schema>();
  private integrationSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;

  user: User | null = null;

  // Font Awesome Pro Light, en objets importés (knowledge/plateforme/icones_fontawesome.md).
  readonly faBars = faBars;
  readonly faXmark = faXmark;
  readonly faAnglesLeft = faAnglesLeft;
  readonly faAnglesRight = faAnglesRight;
  readonly faLifeRing = faLifeRing;

  /**
   * Le tiroir, sous `md` : ouvert ou fermé. Au-dessus, le menu est toujours là
   * sauf s'il est replié.
   */
  readonly menuOpen = signal(false);
  /** À partir de `md` : le menu est masqué et l'écran prend toute la largeur. */
  readonly collapsed = signal(this.readCollapsed());
  readonly signingOut = signal(false);

  private readonly collapseButton = viewChild<ElementRef<HTMLButtonElement>>('collapseButton');
  private readonly expandButton = viewChild<ElementRef<HTMLButtonElement>>('expandButton');

  /**
   * Le tableau de bord, hors groupe : ouvert à quiconque a un espace. Il porte
   * des widgets finance ET opérationnels, filtrés un à un.
   */
  readonly home: MenuItem = {
    label: 'Tableau de bord',
    route: HOME,
    icon: faGrid2
  };

  /**
   * LE menu — un seul, depuis le 2026-09-14, filtré par capacité : chaque
   * entrée déclare la sienne. Il y en avait trois, un par espace, derrière un
   * sélecteur ; cette partition supposait des rôles disjoints, fausse depuis
   * que le gestionnaire exploite le parc et qu'un FINANCE porte aussi MANAGER.
   *
   * Regroupé depuis le 2026-10-13 en trois sections qui recoupent les trois
   * listes de `auth-groups.util.ts` (MANAGER, FINANCE, ADMIN), comme le
   * proposait l'audit UX du 2026-10-07 : un MANAGER voit la première, un
   * FINANCE les deux premières, un ADMIN les trois, et aucune entrée rare ne
   * s'intercale plus entre deux entrées quotidiennes. Une section sans entrée
   * visible disparaît avec son titre.
   *
   * `/manager/leases` n'y est plus, et l'écran non plus : c'était la copie de
   * `/leases` sûre pour un gestionnaire qui n'y avait pas accès. La route a
   * survécu jusqu'au 2026-09-14, où elle est devenue une redirection.
   */
  readonly navGroups: NavGroup[] = [
    {
      // « Gestion » et non « Exploitation », le mot de l'audit : c'est celui du métier (2026-10-13).
      id: 'gestion',
      label: 'Gestion',
      items: [
        { label: 'Propriétaires', route: '/owners', requires: CAPABILITY.OWNER_MANAGE, icon: faUser },
        { label: 'Biens', route: '/properties', requires: CAPABILITY.PROPERTY_MANAGE, icon: faHouse },
        { label: 'Locataires', route: '/tenants', requires: CAPABILITY.TENANT_MANAGE, icon: faUsers },
        { label: 'Baux', route: '/leases', requires: CAPABILITY.LEASE_MANAGE, icon: faFileContract },
        {
          // « Service » et non « Inbox » depuis le 2026-09-03 : l'inbox n'est plus que le
          // PREMIER de ses deux onglets, le second étant les interventions. La route, elle,
          // reste `/manager/tickets` — le renommage porte sur le lieu, pas sur l'objet.
          // La clé à molette, et non l'enveloppe d'avant : celle-ci ne décrivait plus que
          // la moitié de l'écran. L'enveloppe reste le repère de l'onglet Inbox à l'intérieur.
          label: 'Service',
          route: '/manager/tickets',
          requires: CAPABILITY.TICKET_MANAGE,
          icon: faScrewdriverWrench
        },
        { label: 'Checklists', route: '/todos', requires: CAPABILITY.TODO_MANAGE, icon: faListCheck },
        { label: 'Appel de loyers', route: '/rent-calls', requires: CAPABILITY.RENT_CALL_MANAGE, icon: faEnvelope }
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      items: [
        { label: 'Transactions', route: '/transactions', requires: CAPABILITY.TRANSACTION_VIEW, icon: faSplit },
        { label: 'Achats', route: '/purchases', requires: CAPABILITY.PURCHASE_ENCODE, icon: faCartShopping },
        { label: 'Facturation', route: '/invoices', requires: CAPABILITY.INVOICING_MANAGE, icon: faFileInvoice },
        { label: 'Paiements', route: '/payments', requires: CAPABILITY.PAYMENT_MANAGE, icon: faCreditCard }
        // « Imports F.A.S.T » a quitté le menu le 2026-10-13 : l'import n'est pas en
        // service, la carte vit dans les Paramètres, en aperçu (groupe TEST).
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      items: [
        { label: 'Gestionnaires', route: '/managers', requires: CAPABILITY.DIRECTORY_MANAGE, icon: faAward },
        // « Paramètres », le mot des deux autres applications (LP-FIN-APP, latouretpetit) ; la page s'appelle pareil.
        { label: 'Paramètres', route: ADMIN_HOME, requires: CAPABILITY.ADMIN_PANEL, icon: faGear }
      ]
    }
  ];

  currentRoute: string;

  private readonly workingDateService = inject(WorkingDateService);
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly injector = inject(Injector);
  private readonly storage = storageOf(inject(DOCUMENT));
  private readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  /**
   * Les deux palettes, seuls moyens de les ouvrir (2026-09-24) :
   *   Cmd+K        la recherche — baux, propriétaires, locataires, biens ;
   *   Cmd+Shift+K  le graphe du propriétaire de la fiche ouverte.
   * (Ctrl au lieu de Cmd hors Mac.) Écoutés ici, dans la coque des écrans
   * connectés, et actifs même quand un champ a le focus : c'est tout l'intérêt
   * du raccourci. Sur Mac, Ctrl+K reste au champ (effacer jusqu'à la fin de
   * ligne) ; hors Mac, `preventDefault` le reprend à la barre de recherche du
   * navigateur.
   *
   * Réservés à qui peut ouvrir un bail : les quatre fiches que les palettes
   * proposent sont celles de l'exploitation (ADMIN, FINANCE, MANAGER).
   */
  /** Les palettes et leur lanceur : mêmes droits que les raccourcis. */
  get canUsePalettes(): boolean {
    return this.capabilities.has(CAPABILITY.LEASE_MANAGE);
  }

  /**
   * Clic sur le lanceur du bas du menu : ce que fait le raccourci. Le tiroir se
   * referme d'abord, comme après un clic sur une entrée du menu — sans quoi il
   * resterait ouvert derrière la fiche où la palette mène. Sans effet au-dessus
   * de `md`, où il n'y a pas de tiroir.
   */
  openPalette(tab: PaletteTab): void {
    this.closeMenu();
    if (tab === 'graph') this.commandPalette.toggleGraph();
    else this.commandPalette.toggle();
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    const modifier = this.isMac ? event.metaKey : event.ctrlKey;
    if (!modifier || event.altKey || event.repeat) return;
    if (event.key.toLowerCase() !== 'k') return;
    if (!this.canUsePalettes) return;
    event.preventDefault();
    if (event.shiftKey) this.commandPalette.toggleGraph();
    else this.commandPalette.toggle();
  }

  // ---------------------------------------------------------------------------
  // Date de travail
  // ---------------------------------------------------------------------------

  /**
   * La date proposée par les formulaires comptables : la date de travail si
   * l'utilisateur en a posé une, sinon le jour courant. Le champ affiche donc
   * toujours une date — c'est le lien « Aujourd'hui » et la mise en couleur qui
   * distinguent « date figée » de « je suis le jour courant ».
   */
  get workingDateValue(): string {
    return this.workingDateService.effective();
  }

  get workingDateIsSet(): boolean {
    return this.workingDateService.isSet();
  }

  onWorkingDateChange(value: string): void {
    /*
     * Un `<input type="date">` rend une chaîne vide tant que la saisie clavier
     * est incomplète — « 30 » dans le jour, le reste à venir. La prendre pour
     * un effacement remettrait le jour courant dans le champ au milieu de la
     * frappe, en réinitialisant les segments déjà tapés. L'effacement a son
     * propre chemin : le lien « Aujourd'hui ».
     *
     * Écriture non attendue : le profil ne doit pas retenir la barre latérale.
     * Le service met sa valeur en mémoire d'abord et journalise ses échecs.
     */
    if (!value) return;
    void this.workingDateService.set(value);
  }

  clearWorkingDate(): void {
    void this.workingDateService.set(null);
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.currentRoute = this.router.url;
  }

  async ngOnInit(){
    this.getUserData();
    // Non attendu : la barre latérale affiche le jour courant tant que la
    // préférence n'est pas revenue, ce qui est déjà la bonne valeur par défaut.
    void this.workingDateService.ensureLoaded();

    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.urlAfterRedirects;
      this.updateTitle(this.route.root);
    });

    await this.resolveUserGroups();
  }

  ngOnDestroy(): void {
    this.integrationSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Tiroir (sous `md`) et repli (au-dessus)
  // ---------------------------------------------------------------------------

  toggleMenu(): void {
    this.menuOpen.update(open => !open);
  }

  /** Referme le tiroir ; sans effet au-dessus de `md`, où le menu n'est pas un tiroir. */
  closeMenu(): void {
    this.menuOpen.set(false);
  }

  collapse(): void {
    this.setCollapsed(true);
    // Le bouton cliqué disparaît : le focus passe à celui qui ramène le menu.
    afterNextRender(() => this.expandButton()?.nativeElement.focus(), { injector: this.injector });
  }

  expand(): void {
    this.setCollapsed(false);
    afterNextRender(() => this.collapseButton()?.nativeElement.focus(), { injector: this.injector });
  }

  private setCollapsed(collapsed: boolean): void {
    this.collapsed.set(collapsed);
    try {
      this.storage?.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // Stockage bloqué (navigation privée, politique) : le choix n'est juste pas retenu.
    }
  }

  private readCollapsed(): boolean {
    try {
      return this.storage?.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  getUserData(): void {
    this.authService.getUserData().subscribe(
      data => {
        // Cognito attributes use given_name/family_name; normalize to our shape.
        this.user = {
          firstName: data?.given_name ?? data?.firstName ?? '',
          lastName: data?.family_name ?? data?.lastName ?? '',
          email: data?.email ?? ''
        };
      },
      error => {
        console.error('Failed to load user data:', error);
        this.user = { firstName: 'Guest', lastName: '', email: '' };
      }
    );
  }

  /**
   * Un compte sans aucune capacité n'a pas de barre latérale à garnir : ni le
   * tableau de bord, ni aucune section.
   */
  get hasAnyCapability(): boolean {
    return this.capabilities.size > 0;
  }

  /**
   * Les sections que cet utilisateur a le droit de voir, chacune réduite aux
   * entrées dont il porte la capacité ; une section vide disparaît.
   */
  get activeNavGroups(): NavGroup[] {
    if (!this.hasAnyCapability) {
      return [];
    }
    return this.navGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => !item.requires || this.capabilities.has(item.requires))
      }))
      .filter(group => group.items.length > 0);
  }

  /**
   * La date de travail ne concerne que les écrans comptables : elle sert de
   * défaut à la date d'imputation des lignes de répartition. Elle suit donc
   * l'éclatement et non le simple accès au profil — un gestionnaire écrit bien
   * `user_preferences` (ses largeurs de colonnes y vivent), mais n'a aucun
   * formulaire où cette date s'appliquerait.
   */
  get canSeeWorkingDate(): boolean {
    return this.capabilities.has(CAPABILITY.TRANSACTION_SPLIT);
  }

  /** Initials shown in the avatar placeholder. */
  get initials(): string {
    if (!this.user) {
      return '';
    }
    const first = this.user.firstName?.charAt(0) ?? '';
    const last = this.user.lastName?.charAt(0) ?? '';
    const initials = `${first}${last}`.toUpperCase();
    return initials || (this.user.email?.charAt(0).toUpperCase() ?? '');
  }

  /**
   * La déconnexion part vers Cognito (SAML) : la page s'en va. Le bouton se
   * désactive le temps du départ, et revient si `logout()` a avalé une erreur.
   */
  async onLogout(): Promise<void> {
    this.signingOut.set(true);
    try {
      await this.authService.logout();
    } finally {
      this.signingOut.set(false);
    }
  }

  openModal(): void {
    this.closeMenu();
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }

  private updateTitle(route: ActivatedRoute): void {
    let title = 'Application';
    if (route.snapshot.data && route.snapshot.data['title']) {
      title = route.snapshot.data['title'];
    } else if (route.firstChild) {
      this.updateTitle(route.firstChild);
      return;
    }
    this.currentTitle = title;
  }

  private subscribeToIntegrationStatus(): void {
    this.integrationSubscription = this.client.models.TransactionS3.observeQuery().subscribe({
      next: ({ items }) => {
        const unintegrated = items.filter(item => !item.isintegrated);
        this.pendingIntegrationCount = unintegrated.length;
        this.showIntegrationNotification = unintegrated.length > 0;
      },
      error: (err) => {
        console.error('Real-time subscription error for integrations:', err);
      }
    });
  }

  /**
   * Résout les capacités, puis n'ouvre la souscription temps réel que si
   * l'utilisateur y a droit : `TransactionS3` est réservé à ADMIN et FINANCE, un
   * gestionnaire y récolterait des erreurs d'autorisation en boucle.
   */
  private async resolveUserGroups(): Promise<void> {
    this.capabilities = capabilitiesOf(await getUserGroups());

    if (this.capabilities.has(CAPABILITY.TRANSACTION_VIEW)) {
      this.subscribeToIntegrationStatus();
    }
  }
}

function storageOf(document: Document): Storage | null {
  try {
    return document.defaultView?.localStorage ?? null;
  } catch {
    return null;
  }
}
